<template>
  <BaseModal
    close-on-esc-key-click
    v-bind="{ isOpen, title }"
    @close="$emit('close')"
  >
    <div
      v-if="group"
      class="text-sm pt-1"
    >
      <template v-if="showRefund">
        <div
          v-for="(row, index) in rows"
          :key="index"
          class="flex justify-between gap-3 my-2"
        >
          <span class="min-w-0">
            {{ row.label }}
            <span class="block text-grey-600 text-xs whitespace-nowrap">
              {{ row.pricePerUnit.formatted }} × {{ row.quantity }}<template v-if="row.unit">&nbsp;{{ row.unit }}</template>
            </span>
          </span>
          <span class="shrink-0 text-right whitespace-nowrap">
            <template v-if="row.refundPercentage">
              <span class="font-semibold">{{ row.refundedPrice.formatted }}</span>
              <span class="block text-grey-600 text-xs">
                {{ row.totalPrice.formatted }} paid ({{ row.refundPercentage }}% refund)
              </span>
            </template>
            <template v-else>
              <span>Non-refundable</span>
              <span class="block text-grey-600 text-xs">
                {{ row.totalPrice.formatted }} paid
              </span>
            </template>
          </span>
        </div>

        <hr class="my-3 border-0 border-t border-solid border-grey-300">

        <div class="flex justify-between items-baseline gap-3">
          <span class="font-semibold">Total refund</span>
          <span class="shrink-0 text-right whitespace-nowrap">
            <template v-if="combinedRefund.percentage">
              <span class="text-base font-bold">{{ combinedRefund.formatted }}</span>
              <span class="block font-normal text-grey-600 text-xs">
                {{ group.totalPrice.formatted }} paid ({{ combinedRefund.percentage }}% refund)
              </span>
            </template>
            <template v-else>
              <span class="font-semibold">Non-refundable</span>
              <span class="block font-normal text-grey-600 text-xs">
                {{ group.totalPrice.formatted }} paid
              </span>
            </template>
          </span>
        </div>
      </template>

      <template v-else>
        <div
          v-for="(row, index) in rows"
          :key="index"
          class="flex justify-between gap-3 my-2"
        >
          <span class="min-w-0">
            {{ row.label }}
            <span class="text-grey-600 whitespace-nowrap">
              ({{ row.pricePerUnit.formatted }} x {{ row.quantity }}<template v-if="row.unit">&nbsp;{{ row.unit }}</template>)
            </span>
          </span>
          <span class="shrink-0 text-right">
            {{ row.totalPrice.formatted }}
          </span>
        </div>

        <hr class="my-3 border-0 border-t border-solid border-grey-300">

        <div class="flex justify-between font-semibold">
          <span>{{ totalLabel }}</span>
          <span class="text-right">
            {{ group.totalPrice.formatted }}
          </span>
        </div>
      </template>
    </div>
  </BaseModal>
</template>

<script setup lang="ts">
import { computed } from 'vue';

import BaseModal from '@/components/Ui/Modals/BaseModal.vue';

import type { AddonGroupLine } from './price-breakdown.types';
import { resolveAddonUnit } from './resolveAddonUnit';

const props = withDefaults(defineProps<{
  group?: AddonGroupLine | null;
  title?: string;
  totalLabel?: string;
}>(), {
  group: null,
  title: 'Add-on price details',
  totalLabel: 'Add-on total',
});

defineEmits<{
  close: [];
}>();

const isOpen = computed(() => props.group !== null);

const rows = computed(() => (props.group?.items ?? []).map((item) => ({
  ...item,
  unit: resolveAddonUnit(item.pricingUnit, item.quantity),
})));

const showRefund = computed(() => props.group?.refundedPrice != null);

const combinedRefund = computed(() => ({
  formatted: props.group?.refundedPrice?.formatted ?? '',
  percentage: props.group?.refundPercentage ?? 0,
}));
</script>
